import React, { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import type { Band } from '../types/database';
import { dateLocales } from '../i18n';

interface Invitation {
  id: string;
  band: Band;
  instruments: string[];
  created_at: string;
}

export function NotificationBell() {
  const { user } = useAuth();
  const { profile } = useProfile();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const locale = dateLocales[i18n.language as keyof typeof dateLocales] || dateLocales.en;

  useEffect(() => {
    if (!profile) return;

    // Initial fetch
    fetchInvitations();

    // Subscribe to changes
    const channel = supabase
      .channel('invitations')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'invitations',
          filter: `musician_id=eq.${profile.id}`,
        },
        () => {
          fetchInvitations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile]);

  const fetchInvitations = async () => {
    if (!profile) return;

    try {
      const { data, error } = await supabase
        .from('invitations')
        .select(`
          *,
          band:bands!invitations_band_id_fkey(*)
        `)
        .eq('musician_id', profile.id)
        .eq('status', 'pending');

      if (error) throw error;
      setInvitations(data as Invitation[]);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    }
  };

  const handleAccept = async (invitationId: string) => {
    try {
      setLoading(true);
      const { error } = await supabase.rpc('accept_band_invitation', {
        invitation_id: invitationId
      });

      if (error) throw error;
      await fetchInvitations();
    } catch (error) {
      console.error('Error accepting invitation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async (invitationId: string) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('invitations')
        .update({ status: 'declined', updated_at: new Date().toISOString() })
        .eq('id', invitationId);

      if (error) throw error;
      await fetchInvitations();
    } catch (error) {
      console.error('Error declining invitation:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!profile) return null;

  const hasInvitations = invitations.length > 0;

  return (
    <div className="relative">
      <button
        onClick={() => hasInvitations && setIsOpen(!isOpen)}
        className={`relative p-2 transition-colors ${
          hasInvitations 
            ? 'text-light/60 hover:text-light cursor-pointer' 
            : 'text-light/20 cursor-not-allowed'
        }`}
      >
        <Bell className="w-6 h-6" />
        {hasInvitations && (
          <span className="absolute top-0 right-0 w-3 h-3 bg-primary rounded-full" />
        )}
      </button>

      {isOpen && hasInvitations && (
        <div className="absolute right-0 mt-2 w-80 bg-dark border border-gray-custom/20 rounded-lg shadow-lg py-1 z-50">
          <div className="px-4 py-2 border-b border-gray-custom/10">
            <h3 className="font-medium">{t('bands.bandInvitations')}</h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="px-4 py-3 hover:bg-gray-custom/5 border-b border-gray-custom/10 last:border-0"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-light">
                      {invitation.band.name}
                    </p>
                    <p className="text-sm text-light/60 mb-2">
                      {format(new Date(invitation.created_at), 'PPp', { locale })}
                    </p>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {invitation.instruments.map((instrument) => (
                        <span
                          key={instrument}
                          className="px-2 py-0.5 bg-secondary rounded-full text-xs text-light"
                        >
                          {instrument}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleAccept(invitation.id)}
                    disabled={loading}
                    className="flex-1 px-3 py-1 btn btn-secondary text-sm rounded"
                  >
                    {t('bands.accept')}
                  </button>
                  <button
                    onClick={() => handleDecline(invitation.id)}
                    disabled={loading}
                    className="flex-1 px-3 py-1 bg-gray-custom/10 text-light/60 text-sm rounded hover:bg-gray-custom/20 transition-colors disabled:opacity-50"
                  >
                    {t('bands.decline')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}